import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import CoachCodeVerifier from "../../../../auth/Onboarding/CoachCodeVerifier";
import { inchesToFeetAndInches } from "../../../../../utils/utils";
import { stripSeasonSuffix } from "../../../../../utils/teamNames";
import chevron from "../../../../../assets/brand/edit-hub/chevron-14x8.svg";
import { HubCard, FormField, Grid2, HubInput, HubTextArea, HubSelect, TagInput, Combobox, SecondaryBtn, OutlineBadge, useEditLayout } from "../editHubPieces";
import { splitCityOnBlur } from "../../onboarding/hometownFields";
import { TeamsCard, genderLetter, orderTeams, teamSubline, teamTitle } from "./TeamsCard";
import { searchTeams, setMyTeamSlot } from "../editHubServices";
import { COLLEGE_LEVELS, collegeLevelOf, hasTeam, isMale } from "../editHubModel";

// The roster step a Club or Team pick opens reuses the onboarding steps,
// which are fetched only when it opens (as TeamsCard's "Add a team").
const TeamAddFlow = lazy(() => import("./TeamAddFlow"));

const initialsOf = (a) => `${(a.name || "")[0] || ""}${(a.surname || "")[0] || ""}`.toUpperCase() || "?";

const codeOptions = (list) => list.map((c) => <option key={c.value} value={c.value}>{c.name}</option>);

// The bio source switch. Phones (Figma 2085:1259 / 2085:1263): the label
// with the "Recommended" badge beside it, then a full-width 52px segmented
// control whose selected half is white. Desktop keeps the dark pill row.
const BIO_SOURCES = [["cache", "Use Cache-generated", "Cache-generated"], ["manual", "Write my own", "Write my own"]];
const BioSource = ({ source, set }) => {
  const { phone } = useEditLayout();
  if (phone) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="bm-label-l text-bm-ink">Bio source</span>
          <OutlineBadge>Recommended</OutlineBadge>
        </div>
        <div role="group" aria-label="Bio source" className="flex h-[52px] w-full items-center rounded-full bg-bm-mutedbg p-1">
          {BIO_SOURCES.map(([v, , label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={source === v}
              onClick={() => set({ bioSource: v })}
              className={`flex h-11 min-w-0 flex-1 items-center justify-center rounded-full px-3 bm-label-l ${source === v ? "bg-white text-bm-ink shadow-[0_2px_6px_rgba(0,0,0,0.08)]" : "text-bm-muted"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="bm-label-l text-bm-ink">Bio source</span>
      <div className="flex h-10 items-center rounded-full bg-bm-mutedbg p-[3px]">
        {BIO_SOURCES.map(([v, label]) => (
          <button key={v} type="button" onClick={() => set({ bioSource: v })} className={`flex h-[34px] items-center rounded-full px-4 bm-label-xs ${source === v ? "bg-bm-n700 text-white" : "text-bm-muted"}`}>{label}</button>
        ))}
      </div>
      <OutlineBadge>Recommended</OutlineBadge>
    </div>
  );
};

// Numeric keypads on phones (a plain number pad, or one with a decimal
// point); desktop fields stay as they are.
const keypad = (phone, mode) => (phone ? { inputMode: mode } : {});

// v2 · 01 · Photo & bio. `editingOther` (a Circle member editing): the photo
// can be replaced but only the athlete removes it.
export const PhotoBioSection = ({ a, set, interestsKnown, avatarBusy, onAvatarUpload, onAvatarDelete, editingOther = false }) => {
  const { phone } = useEditLayout();
  const fileRef = useRef(null);
  const source = a.bioSource || "cache";
  return (
    <>
      <HubCard>
        <div className="flex items-center gap-5">
          {a.avatar ? (
            <img src={a.avatar} alt="" className="h-24 w-24 shrink-0 rounded-[16px] border border-bm-orange object-cover" />
          ) : (
            <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[16px] border border-bm-orange bg-white bm-h-l text-bm-orange">{initialsOf(a)}</span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {/* Phones wrap Remove under the button when the column is too
                narrow for both (below about 350px). */}
            <div className={phone ? "flex flex-wrap items-center gap-x-3 gap-y-2" : "flex items-center gap-3"}>
              <SecondaryBtn onClick={() => fileRef.current?.click()} disabled={avatarBusy}>{avatarBusy ? "Uploading…" : a.avatar ? "Replace photo" : "Upload photo"}</SecondaryBtn>
              {a.avatar && !editingOther && <button type="button" onClick={onAvatarDelete} disabled={avatarBusy} className="bm-label-l text-bm-muted hover:text-bm-critical">Remove</button>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onAvatarUpload(f); e.target.value = ""; }} />
            <p className="bm-body-s text-bm-tertiary">JPG or PNG, up to 5 MB. A recent, well-lit photo is what coaches and brands see first.</p>
          </div>
        </div>
        <BioSource source={source} set={set} />
        <FormField label="Player Bio" optional hint={source === "manual" ? "A few sentences on your story, goals, what makes you stand out." : "Auto-generated by Cache AI from your profile. It refreshes when you save."}>
          <HubTextArea
            value={a.bio || ""}
            readOnly={source !== "manual"}
            placeholder={source === "manual" ? "A few sentences on your story, goals, what makes you stand out." : "Your Cache-generated bio will appear here after you save."}
            className={source !== "manual" ? "!bg-bm-subtle text-bm-muted" : ""}
            onChange={(e) => set({ bio: e.target.value })}
          />
        </FormField>
      </HubCard>
      <HubCard title="Interests" desc="Outside of sport. Brands use these to find athletes who fit their audience.">
        <FormField label="Interests" optional hint="Type an interest and press Enter or comma to add it.">
          <TagInput values={a.interests || []} onChange={(v) => set({ interests: v })} suggestions={interestsKnown} />
        </FormField>
      </HubCard>
    </>
  );
};

const pad2 = (n) => String(n).padStart(2, "0");

// The local calendar date `years` years before `now`, as YYYY-MM-DD. Feb 29
// falls back to Feb 28 in a year without it.
const yearsBefore = (years, now) => {
  const y = now.getFullYear() - years;
  const m = now.getMonth();
  const day = Math.min(now.getDate(), new Date(y, m + 1, 0).getDate());
  return `${y}-${pad2(m + 1)}-${pad2(day)}`;
};

// The date of birth picker's range: at least 13 years old and at most 100,
// the rule the server applies at sign-up.
export const dobBounds = (now = new Date()) => ({ min: yearsBefore(100, now), max: yearsBefore(13, now) });

// v2 · 02 · Personal details
// On phones the date field carries the select chevron (Figma 2085:1287); the
// browser's own picker button is see-through and sits under it, so a click
// on the chevron still opens the picker.
const PHONE_DATE = "appearance-none [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:-mr-[18px] [&::-webkit-calendar-picker-indicator]:w-11 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0";

export const PersonalSection = ({ a, set, opts, fieldErrors = {} }) => {
  const { phone } = useEditLayout();
  const dob = dobBounds();
  const dobInput = <HubInput type="date" aria-label="Date of birth" value={a.dateOfBirth ? String(a.dateOfBirth).slice(0, 10) : ""} min={dob.min} max={dob.max} onChange={(e) => set({ dateOfBirth: e.target.value })} className={phone ? PHONE_DATE : ""} />;
  return (
    <HubCard>
      <Grid2>
        <FormField label="Name" pair error={fieldErrors.name}><HubInput value={a.name || ""} onChange={(e) => set({ name: e.target.value })} /></FormField>
        <FormField label="Surname" pair error={fieldErrors.surname}><HubInput value={a.surname || ""} onChange={(e) => set({ surname: e.target.value })} /></FormField>
        <FormField label="Date of birth" error={fieldErrors.dateOfBirth}>
          {phone ? (
            <span className="relative block">
              {dobInput}
              <img src={chevron} alt="" className="pointer-events-none absolute right-[18px] top-1/2 h-2 w-[14px] -translate-y-1/2" />
            </span>
          ) : dobInput}
        </FormField>
        <FormField label="Gender"><HubSelect value={a.gender ?? ""} onChange={(e) => set({ gender: e.target.value ? parseInt(e.target.value, 10) : "" })}><option value="">Select</option>{codeOptions(opts.genders)}</HubSelect></FormField>
        <FormField label="Country represented"><HubSelect value={a.countryRepresented ?? ""} onChange={(e) => set({ countryRepresented: e.target.value ? parseInt(e.target.value, 10) : "" })}><option value="">Select</option>{codeOptions(opts.countries)}</HubSelect></FormField>
        <FormField label="Phone number" error={fieldErrors.phoneNumber}><HubInput inputMode="tel" placeholder="+1 (702) 123-4567" value={a.phoneNumber || ""} onChange={(e) => set({ phoneNumber: e.target.value })} /></FormField>
        <FormField label="Hometown city" pair hint="Just the city. Pick the state beside it."><HubInput aria-label="Hometown city" value={a.homeTown || ""} placeholder="Chandler" onChange={(e) => set({ homeTown: e.target.value })} onBlur={(e) => { const out = splitCityOnBlur(e.target.value, a.state, opts.states); if (out) set(out); }} /></FormField>
        <FormField label="State" pair><HubSelect aria-label="State" value={a.state ?? ""} onChange={(e) => set({ state: e.target.value ? parseInt(e.target.value, 10) : "" })}><option value="">Select</option>{codeOptions(opts.states)}</HubSelect></FormField>
      </Grid2>
    </HubCard>
  );
};

const num = (raw, integer = false) => {
  if (raw === "" || raw == null) return "";
  const v = integer ? parseInt(raw, 10) : parseFloat(raw);
  return Number.isNaN(v) ? "" : v;
};

// v2 · 03 · Sport. On phones jersey takes the full width and height and
// weight sit two-up (Figma 2085:1417). Weight is asked of men only (Kobi,
// 2026-10-07: "Weight (males only)"): anyone else can still enter it, and
// it is marked Optional for them (no completion line).
export const SportSection = ({ a, set, opts }) => {
  const { phone } = useEditLayout();
  const weightAsked = isMale(a);
  return (
    <>
      <HubCard>
        <Grid2>
          <FormField label="Primary sport"><HubSelect value={a.primarySport ?? ""} onChange={(e) => set({ primarySport: e.target.value ? parseInt(e.target.value, 10) : "", position: [] })}><option value="">Select</option>{codeOptions(opts.sports)}</HubSelect></FormField>
          <FormField label="Level of play"><HubSelect value={a.levelOfPlay ?? ""} onChange={(e) => set({ levelOfPlay: e.target.value ? parseInt(e.target.value, 10) : "" })}><option value="">Select</option>{codeOptions(opts.levels)}</HubSelect></FormField>
        </Grid2>
        <FormField label="Position(s)" hint={opts.positions.length ? "Pick one or more. Your first position leads on your public profile." : "Pick a primary sport to choose positions."}>
          <TagInput values={Array.isArray(a.position) ? a.position : []} onChange={(v) => set({ position: v })} options={opts.positions} />
        </FormField>
        <div className={phone ? "grid grid-cols-2 gap-x-3 gap-y-6" : "grid gap-5 sm:grid-cols-3"}>
          <FormField label="Jersey number" optional className={phone ? "col-span-2" : ""}><HubInput type="number" min={0} {...keypad(phone, "numeric")} value={a.jerseyNumber ?? ""} onChange={(e) => set({ jerseyNumber: num(e.target.value, true) })} /></FormField>
          <FormField label="Height (in)" hint={a.height > 0 ? `${inchesToFeetAndInches(a.height)} (ft/in)` : undefined}><HubInput type="number" min={0} {...keypad(phone, "decimal")} value={a.height || ""} onChange={(e) => set({ height: num(e.target.value) })} /></FormField>
          <FormField label="Weight (lbs)" optional={!weightAsked} hint={weightAsked && !a.weight ? "No weight on file." : undefined}><HubInput type="number" min={0} {...keypad(phone, "decimal")} placeholder="e.g. 180" value={a.weight || ""} onChange={(e) => set({ weight: num(e.target.value) })} /></FormField>
        </div>
      </HubCard>
      <HubCard title="Other sports" desc="Additional sports you play. These contribute to your IRL score.">
        <FormField label="Other sports" optional>
          <TagInput values={Array.isArray(a.otherSports) ? a.otherSports : []} onChange={(v) => set({ otherSports: v })} options={opts.otherSports} />
        </FormField>
      </HubCard>
    </>
  );
};

// ---- The Club and Team fields pick real teams (notes of 10/19, K4) ------------
// A field lists, as the athlete types: their own teams of the field's kind
// first, then the team search (every league and CACHE's own teams, squads
// with their age group), then "Use this name only", today's typed name,
// which links no team. Picking one of their teams fills the field from it
// at once (athletes/teams/slot/); a team from the search opens the roster
// step for the athlete's row, then fills the field. The server makes the
// team a binding first (the main team when there is none, else another
// team), writes the field from it and moves the value it replaces to the
// field's history. A typed name still saves as a name on blur or Enter.
// `prepare` (the editor's) runs just before the link, so a save of the
// names from before never lands over the field the server writes.
// What a limited search says (the answer's dataStatus, as the wizard's
// search step says it): the team feed down, so only the teams already on
// CACHE, or a saved answer.
const SEARCH_LIMITED = "Team search is limited right now. Showing teams already on CACHE.";
const SEARCH_SAVED = "Team search is limited right now. Showing saved results.";
const limitNoteOf = (dataStatus) => {
  const state = dataStatus && typeof dataStatus === "object" ? dataStatus.state : "";
  if (!state || state === "live") return "";
  return state === "stale" ? SEARCH_SAVED : SEARCH_LIMITED;
};
const SEARCH_MIN = 2;
const SEARCH_WAIT_MS = 350;
const LIST_CLASS = "absolute left-0 top-full z-20 mt-1 max-h-[280px] w-full overflow-y-auto rounded-[12px] border border-bm-border bg-white p-1 shadow-[0px_8px_24px_rgba(0,0,0,0.08)]";
const ROW_CLASS = "flex w-full flex-col items-start gap-[2px] rounded-[8px] px-3 py-2 text-left hover:bg-bm-subtle disabled:opacity-50";
const slotKey = (t) => `${t.league || ""}|${t.teamId || t.id || ""}`;
// A search row's line: its league (no season), city and latest season.
const searchMeta = (t) => [stripSeasonSuffix(t.leagueName || ""), t.city, t.season ? `Latest season ${t.season}` : ""].filter(Boolean).join(" · ");

const SlotRow = ({ title, sub, onPick, disabled }) => (
  <li>
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onPick} disabled={disabled} className={ROW_CLASS}>
      <span className="bm-body-m text-bm-ink">{title}</span>
      {sub && <span className="bm-body-s text-bm-muted">{sub}</span>}
    </button>
  </li>
);

export const TeamSlotPicker = ({ label, slot, value, onChange, ownTeams = [], gender = "", athlete = {}, onLinked, prepare, placeholder = "Select or type a team" }) => {
  const { phone } = useEditLayout();
  const [draft, setDraft] = useState(value || "");
  const [open, setOpen] = useState(false);
  // True from a keystroke until the athlete picks, keeps the text or
  // leaves the field: the list then follows what they typed.
  const [editing, setEditing] = useState(false);
  // The search: its rows (null before one ran), whether it is running and
  // what it says when it was limited (the team feed down), else "".
  const [found, setFound] = useState(null);
  const [searching, setSearching] = useState(false);
  const [limited, setLimited] = useState("");
  // The search row whose roster step is open, and the own team being linked.
  const [picked, setPicked] = useState(null);
  const [linking, setLinking] = useState("");
  const [problem, setProblem] = useState("");
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const latest = useRef(0);
  const live = useRef(true);
  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => {
    live.current = true;
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { live.current = false; clearTimeout(timer.current); document.removeEventListener("mousedown", close); };
  }, []);

  const q = draft.trim();
  // What the athlete typed, not the name the field holds already.
  const typed = editing ? q : "";
  const own = (Array.isArray(ownTeams) ? ownTeams : []).filter((t) => !typed || teamTitle(t).toLowerCase().includes(typed.toLowerCase()));

  // Search a moment after the last keystroke; an answer to an older query
  // is dropped. A failed search reads as a limited one.
  const search = (text) => {
    clearTimeout(timer.current);
    const term = text.trim();
    const mine = ++latest.current;
    if (term.length < SEARCH_MIN) { setFound(null); setSearching(false); setLimited(""); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      let rows = [];
      let note = "";
      try {
        rows = await searchTeams(term, gender || undefined);
        note = limitNoteOf(rows?.dataStatus);
      } catch {
        note = SEARCH_LIMITED;
      }
      if (!live.current || latest.current !== mine) return;
      setFound(Array.isArray(rows) ? rows.slice(0, 8) : []);
      setLimited(note);
      setSearching(false);
    }, SEARCH_WAIT_MS);
  };
  const stopSearch = () => { clearTimeout(timer.current); latest.current += 1; setSearching(false); };
  // The list closes and the typed text is no longer a search.
  const done = () => { stopSearch(); setOpen(false); setEditing(false); };

  // The typed text as the field's name, linking no team.
  const keepName = (text) => {
    done();
    if (text !== (value || "")) onChange(text);
  };
  // Focus left the field and its list: the typed text is the field's name.
  // Focus moving from the field into its own list (a keyboard user tabbing
  // to a row) is not leaving it, so a row picked from the keyboard never
  // saves the typed text beside the team, and never saves the field's name
  // again (a name the editor holds back stays held back).
  const leave = () => {
    const text = draft.trim();
    if (text !== (value || "")) onChange(text);
  };
  // One of the athlete's own teams: the field is filled from it at once.
  const pickOwn = async (t) => {
    done();
    setProblem("");
    // The typed text is not the field's name: nothing saves it on blur.
    setDraft(value || "");
    setLinking(slotKey(t));
    let undo = null;
    try {
      if (typeof prepare === "function") undo = await prepare();
      const data = await setMyTeamSlot({ slot, teamId: t.teamId, league: t.league, teamSource: t.teamSource || "", teamName: t.teamName, leagueLabel: stripSeasonSuffix(t.leagueLabel || "") });
      // Linked: the editor takes the fields the server filed from here.
      undo = null;
      if (live.current && onLinked) await onLinked(data, t);
    } catch (e) {
      // Refused: the names go with saves as before.
      if (typeof undo === "function") undo();
      if (live.current) setProblem(e?.message || "We could not link this team.");
    } finally {
      if (live.current) setLinking("");
    }
  };
  // A team from the search: its roster step opens below the field.
  const pickFound = (row) => {
    done();
    setProblem("");
    setDraft(value || "");
    setPicked(row);
  };

  const showSearch = typed.length >= SEARCH_MIN;
  const hasList = open && (own.length > 0 || showSearch || !!typed);
  return (
    // Focus leaving the field and its list closes the list; the typed text
    // stays as the field's name (saved by the field's own blur, or here
    // when focus leaves from a row of the list).
    <div
      ref={boxRef}
      className="relative"
      onBlur={(e) => {
        if (boxRef.current?.contains(e.relatedTarget)) return;
        done();
        if (e.target !== inputRef.current && editing) leave();
      }}
    >
      <div className="relative">
        <input
          ref={inputRef}
          className={phone ? "bm-input !pr-12" : "bm-input !pr-10"}
          aria-label={label}
          value={draft}
          placeholder={placeholder}
          disabled={!!linking}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); setEditing(true); setProblem(""); search(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={(e) => { if (!boxRef.current?.contains(e.relatedTarget)) leave(); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); keepName(draft.trim()); }
            if (e.key === "Escape") done();
          }}
        />
        <button type="button" tabIndex={-1} aria-label="Open list" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)} className={phone ? "absolute right-0 top-0 flex h-14 w-12 items-center justify-end pr-[18px]" : "absolute right-0 top-0 flex h-10 w-10 items-center justify-center"}>
          <img src={chevron} alt="" className="h-2 w-[14px]" />
        </button>
      </div>
      {hasList && (
        <ul className={LIST_CLASS} aria-label={`${label} choices`}>
          {own.length > 0 && <li className="px-3 pb-1 pt-2 bm-overline">Your teams</li>}
          {own.map((t) => (
            <SlotRow key={`own-${slotKey(t)}`} title={teamTitle(t) || "Team"} sub={teamSubline(t, [], false)} onPick={() => pickOwn(t)} disabled={!!linking} />
          ))}
          {showSearch && <li className="px-3 pb-1 pt-2 bm-overline">Teams</li>}
          {showSearch && limited && <li className="px-3 py-1 bm-body-s text-bm-muted">{limited}</li>}
          {showSearch && searching && <li className="px-3 py-2 bm-body-s text-bm-muted">Searching...</li>}
          {/* A limited search says so above, never "No team matched". */}
          {showSearch && !searching && !limited && found && found.length === 0 && <li className="px-3 py-2 bm-body-s text-bm-muted">No team matched.</li>}
          {showSearch && !searching && (found || []).map((row) => (
            <SlotRow key={`found-${row.cacheTeamId || row.id}|${row.leagueKey || row.cacheLeague || ""}`} title={teamTitle(row) || row.name} sub={searchMeta(row)} onPick={() => pickFound(row)} />
          ))}
          {typed && <SlotRow title={`Use "${typed}" only`} sub="Not linked to a team" onPick={() => keepName(typed)} />}
        </ul>
      )}
      {linking && <p role="status" className="pt-2 bm-body-s text-bm-muted">Linking your team...</p>}
      {problem && <p role="alert" className="pt-2 bm-body-s text-bm-critical">{problem}</p>}
      {picked && (
        <div className="mt-3 flex flex-col gap-4 rounded-[12px] bg-bm-subtle p-4">
          <Suspense fallback={<span className="bm-body-m text-bm-muted">Loading...</span>}>
            <TeamAddFlow
              athlete={athlete}
              gender={gender}
              slot={slot}
              prepare={prepare}
              initialTeam={picked}
              onAdded={async (data) => { const row = picked; setPicked(null); if (onLinked) await onLinked(data, row); }}
              onCancel={() => setPicked(null)}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
};

// The teams on the profile, read only, for a Circle member: the team list
// and the team codes act on the signed-in athlete's own account, so only the
// athlete adds, removes or verifies a team. Titled apart from the section
// ("Teams"), so a screen has one heading of that name.
const TeamsOverview = ({ a }) => {
  // Every team on the profile (the load's allTeams), else the verified ones.
  const teams = orderTeams(Array.isArray(a.allTeams) && a.allTeams.length
    ? a.allTeams
    : (Array.isArray(a.verifiedTeams) ? a.verifiedTeams : []).map((t) => ({ ...t, verified: true })));
  const first = a.name || "The athlete";
  return (
    <HubCard title="Teams on the profile" desc={`${first} adds, removes and verifies teams from their own account.`}>
      {teams.length === 0 ? (
        <p className="bm-body-m text-bm-muted">No team on the profile yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-bm-mutedbg rounded-[12px] border border-bm-mutedbg" aria-label="Teams on the profile">
          {teams.map((t, i) => (
            <li key={`${t.league || ""}|${t.teamId || t.teamName || i}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="truncate bm-label-l text-bm-ink">{teamTitle(t) || "Team"}</span>
                {teamSubline(t, a.allTeams, false) && <span className="truncate bm-body-s text-bm-muted">{teamSubline(t, a.allTeams, false)}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {t.isPrimary && <OutlineBadge>Primary</OutlineBadge>}
                {typeof t.verified === "boolean" && <OutlineBadge>{t.verified ? "Verified" : "Unverified"}</OutlineBadge>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </HubCard>
  );
};

// The Team field's label by level: what it holds, so the level still shows.
const TEAM_LABELS = { 1: "High school team", 2: "College team", 3: "Pro team" };

// The athlete's own teams each field offers first: the Club their clubs;
// the Team the kind their level files there (a high school team, a college
// team, a pro's team), else any team that is not a club. Teams with an id
// only (a name-only listing cannot fill a field).
const ownTeamsFor = (a, field, level) => {
  const kinds = field === "club" ? ["club"] : level === 1 ? ["scholastic"] : level === 2 ? ["college"] : level === 3 ? ["pro", "club"] : ["scholastic", "college", "pro"];
  return (Array.isArray(a.allTeams) ? a.allTeams : []).filter((t) => t && String(t.teamId || "").trim() && kinds.includes(t.kind));
};

// v2 · 04 · Teams (Kobi, 2026-10-07; was "School & team"), three cards:
//  - the current team: Team, labelled by level (a high school or college
//    team saves as previousSchool, a pro's team as currentTeam); Club, not
//    for a pro (their Team is it) and Optional for a college athlete or
//    anyone who has a team (editHubModel: only what the hub asks for
//    counts); each picks a real team (TeamSlotPicker; a Circle member types
//    names only); then the class: the high school graduation year (what
//    Cerebro has, for college athletes too) and a college athlete's college
//    level;
//  - Previous teams: the last high school and a pro's University, each a
//    line, then the previous high schools, colleges (universities for a
//    pro) and clubs, filled from what we found until the athlete edits one;
//  - Your teams (TeamsCard: the team list, Add a team and the team code
//    box), which a Circle member does not get: see TeamsOverview.
export const SchoolSection = ({ a, names, set, setNames, opts, onTeamLinked, onTeamsChanged, onTeamAdded, onTeamSlotSet, beforeTeamWrite, editingOther = false }) => {
  const { phone } = useEditLayout();
  // Changes when a field linked a team: the Teams card reads its list again.
  const [teamsVersion, setTeamsVersion] = useState(0);
  const level = Number(a.levelOfPlay) || 0;
  const college = level === 2;
  const pro = level === 3;
  const teamLabel = TEAM_LABELS[level] || "Team";
  // A pro's Team is their pro team, saved in the Club field (currentTeam).
  const teamKey = pro ? "clubTeam" : "school";
  const teamOptions = (level === 1 ? opts.highSchools : college ? opts.colleges : pro ? opts.teams : opts.schools) || [];
  const verified = Array.isArray(a.verifiedTeams) && a.verifiedTeams.length ? a.verifiedTeams : a.status === "verified" ? [{ teamName: a.currentTeamName || "", leagueLabel: "", isPrimary: true }] : [];
  // The profile estimates a college level from the class year when none is
  // on file; the hub says so and still asks for the athlete's pick.
  const estimated = a.identity?.collegeLevel?.estimated && !collegeLevelOf(a.collegeLevel) ? a.identity.collegeLevel.label : "";
  const gradYear = (
    <FormField label="High school graduation year" className={college ? "" : "sm:w-[325px]"}>
      <HubInput type="number" min={1900} max={2100} aria-label="High school graduation year" {...keypad(phone, "numeric")} value={a.gradYear || ""} onChange={(e) => set({ gradYear: num(e.target.value, true) })} />
    </FormField>
  );
  // The team search reads the main team's gender, else the profile's.
  const genderName = (opts.genders || []).find((g) => g.value === a.gender)?.name || "";
  const mainTeam = (Array.isArray(a.allTeams) ? a.allTeams : []).find((t) => t && t.isPrimary);
  const searchGender = genderLetter(mainTeam?.gender) || genderLetter(genderName);
  const linked = async (data, label, team) => {
    setTeamsVersion((v) => v + 1);
    if (onTeamSlotSet) await onTeamSlotSet(data, label, team);
  };
  // The Team field (a pro's is their pro team, filed as the Club) and the
  // Club field; a Circle member types names only (team links act on the
  // signed-in account). `field` says which own teams it offers ('team' or
  // 'club'), `slot` where the server files the pick.
  const teamField = (key, label, field, slot, options, placeholder) => (editingOther
    ? <Combobox label={label} value={names[key]} onChange={(v) => setNames({ [key]: v })} options={options} placeholder={placeholder} />
    : (
      <TeamSlotPicker
        label={label}
        slot={slot}
        value={names[key]}
        onChange={(v) => setNames({ [key]: v })}
        ownTeams={ownTeamsFor(a, field, level)}
        gender={searchGender}
        athlete={a}
        onLinked={(data, team) => linked(data, label, team)}
        prepare={beforeTeamWrite}
        placeholder={placeholder}
      />
    ));
  // One previous-teams list (TagInput): saved once the athlete edits it.
  const history = (key, label, suggestions) => (
    <FormField label={label} optional>
      <TagInput values={names[key] || []} onChange={(v) => setNames({ [key]: v })} suggestions={suggestions || []} />
    </FormField>
  );
  return (
    <>
      <HubCard>
        <Grid2>
          <FormField label={teamLabel}>{teamField(teamKey, teamLabel, "team", pro ? "club" : "team", teamOptions, "Select or type your team")}</FormField>
          {!pro && (
            <FormField label="Club" optional={college || hasTeam(a, names)} hint="Your club or travel team, if you play for one.">
              {teamField("clubTeam", "Club", "club", "club", opts.teams || [], "Select or type a team")}
            </FormField>
          )}
        </Grid2>
        {college ? (
          <Grid2>
            {gradYear}
            <FormField label="College level" hint={estimated ? `Estimated as ${estimated} from the high school graduation year. Pick the college level to confirm it.` : undefined}>
              <HubSelect aria-label="College level" value={collegeLevelOf(a.collegeLevel)} onChange={(e) => set({ collegeLevel: e.target.value })}>
                <option value="">Select</option>
                {COLLEGE_LEVELS.map(([code, name]) => <option key={code} value={code}>{`${code} (${name})`}</option>)}
              </HubSelect>
            </FormField>
          </Grid2>
        ) : gradYear}
      </HubCard>
      <HubCard title="Previous teams" desc="Teams you played for before. They stay on your profile as history.">
        {level >= 2 && (
          <Grid2>
            <FormField label="Last high school"><Combobox label="Last high school" value={names.lastHighSchool} onChange={(v) => setNames({ lastHighSchool: v })} options={opts.highSchools || []} placeholder="Select or type a school" /></FormField>
            {pro && <FormField label="University"><Combobox label="University" value={names.school} onChange={(v) => setNames({ school: v })} options={opts.schools || []} placeholder="Select or type a school" /></FormField>}
          </Grid2>
        )}
        {history("previousHighSchools", "Previous high schools", opts.highSchools)}
        {level >= 2 && history("previousColleges", pro ? "Previous universities" : "Previous colleges", opts.colleges)}
        {history("previousClubTeams", "Previous clubs", opts.teams)}
      </HubCard>
      {/* One card for every team: the list with its badges, "Add a team" and
          the team code box ("Verify a team"). The verified teams are not
          listed a second time. */}
      {editingOther ? (
        <TeamsOverview a={a} />
      ) : (
        <TeamsCard
          a={a}
          genderName={genderName}
          onTeamsChanged={onTeamsChanged}
          onTeamAdded={onTeamAdded}
          beforeTeamWrite={beforeTeamWrite}
          version={teamsVersion}
          verifier={<CoachCodeVerifier v2 verifiedTeams={verified} onLinked={onTeamLinked} />}
        />
      )}
    </>
  );
};
